import React from "react";
import { NotAcceptingOrdersBannerContent } from "./notAcceptingOrdersBannerContent";
import { Merchant } from "@/dto/merchant";
import { useTranslations } from "next-intl";
import { DayPart } from "@/dto/dayPartMenu";
import { HHMMtoDate } from "@/utils/dateTimeUtils";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import { useSearchParams } from "next/navigation";
import { OrderType } from "@/constants/enum";
import {
  getMenuDisplayName,
  findNextAvailableDay,
} from "@/utils/dayPartMenuUtils";
import { isEmpty, isNil } from "lodash";
import { DateTime } from "luxon";
import {
  createSchedulingContext,
  getMerchantBusinessHours,
  getSchedulableDateOptions,
  isWithinBusinessHours,
} from "@/utils/scheduleOrderUtils";
import { TFunction } from "@/types";
import { getFromSessionStorage } from "@/utils/sessionStorageUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { OrderEstimates } from "@/dto/orderEstimates";

export const NotAcceptingOrdersBanner = ({
  merchant,
  isNotAcceptingOrder = false,
  selectedDayPart = null,
}: {
  merchant: Merchant;
  isNotAcceptingOrder: boolean;
  selectedDayPart?: DayPart | null;
}) => {
  const t = useTranslations("NotAcceptingOrdersBanner");
  const tPickup = useTranslations("CalculatePickTime");
  const searchParams = useSearchParams();

  // 1. Core Guard: if no merchant, we can't render anything
  if (!merchant) return null;

  // 2. Logic to determine the message (Extracted to keep complexity low)
  const message = getBannerMessage(
    t,
    merchant,
    selectedDayPart,
    searchParams,
    tPickup
  );

  // 3. Final Render Check
  if (isNotAcceptingOrder) {
    return (
      <NotAcceptingOrdersBannerContent merchant={merchant} message={message} />
    );
  }

  return null;
};

/**
 * Helper: Logic to determine the specific banner message
 * Cognitive Complexity is low here due to flat structure
 */
function getBannerMessage(
  t: any,
  merchant: Merchant,
  selectedDayPart: any,
  searchParams: any,
  tPickup: TFunction
) {
  if (isEmpty(selectedDayPart)) return null;

  const orderType = searchParams?.get("order_type");
  const isDineIn = orderType?.toLowerCase() === OrderType.DINE_IN.toLowerCase();

  const isOpen = isBusinessOpen(merchant, isDineIn);
  const isAvailable = merchant.isBusinessHourAvailable;

  // If business isn't open/available, the message logic won't trigger
  if (!isOpen || !isAvailable) return null;

  const businessHours = getMerchantBusinessHours(merchant);
  const nowInMerchantTimeZone = DateTime.fromJSDate(new Date(), {
    zone: merchant.timezone,
  });
  const isNowInHours = isWithinBusinessHours(
    nowInMerchantTimeZone,
    businessHours,
    merchant.timezone
  );

  // Case: Outside Business Hours
  if (!isNowInHours) {
    return t("menuNotAvailable");
  }

  const context = createSchedulingContext({
    merchant,
    orderType: orderType as OrderType,
    orderEstimates: getFromSessionStorage<Partial<OrderEstimates>>(
      SessionStorageKey.ORDER_ESTIMATES,
      {}
    ),
    daypartMenu: selectedDayPart,
    hoursOverride: businessHours ?? null,
  });

  const schedulableDateOptions = getSchedulableDateOptions({
    context,
    scheduleDateRange: merchant?.scheduledOrdering.maxDays - 1,
    t: tPickup,
  });

  // Case: Cannot schedule any orders due to business hours or daypart time lines
  if (isEmpty(schedulableDateOptions)) {
    return t("menuNotAvailable");
  }

  // Case: Inside Business Hours but specific menu is not active
  const isActiveDayPartMenu = selectedDayPart?.activeNow ?? true;
  if (!isActiveDayPartMenu) {
    const nextAvailableDay = findNextAvailableDay(
      selectedDayPart,
      merchant.timezone
    );
    const messageCode = nextAvailableDay
      ? "menuAvailableOn"
      : "menuAvailableAt";

    return t(messageCode, {
      dayPartName: getMenuDisplayName(selectedDayPart?.menuName, t),
      dayPartStartTime: formatStartTime(
        selectedDayPart?.time?.startTime,
        merchant.timezone
      ),
      day: nextAvailableDay ?? "",
    });
  }

  return null;
}

/**
 * Helper: Formatting logic isolated to avoid 'nesting' penalties
 */
function formatStartTime(startTime: any, timezone: string) {
  if (isNil(startTime)) return "";

  return HHMMtoDate(
    startTime.toString().padStart(4, "0"),
    timezone,
    new Date()
  ).toLocaleString({
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}
